import { createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import * as RoutesModule from 'Routes';
import { AuthenticationService } from 'services/AuthenticationService';
import { configure } from 'mobx';
import { ConfigurationService } from 'services/ConfigurationService';
import App from './App';
import registerServiceWorker from './registerServiceWorker';

// global components
import { AppErrorBoundary } from 'components/ui/AppErrorBoundary';

// axios
import type { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';

// Stores
import { NotificationsStore } from 'stores/NotificationsStore';
import { OrganizationsStore } from 'stores/OrganizationsStore';
import { UsersStore } from 'stores/UsersStore';
import ConfirmDiscardingChangesDialogStore from 'stores/ConfirmDiscardingChangesDialogStore';
import { InviteStore } from 'stores/InviteStore';
import AnalyticsStore from './stores/AnalyticsStore';
import {RoleStore} from "./stores/RoleStore";
import StartupService from "./services/StartupService";
import StartupStore from "./stores/StartupStore";
import {translationEn} from "./locales";
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { ToursStore } from './stores/ToursStore';


const rootElement = document.getElementById('root');

registerServiceWorker();

// Configure MOBX to require Actions to mutate the state always
configure({ enforceActions: 'observed' });

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  confirmDiscardingChangesDialogStore: ConfirmDiscardingChangesDialogStore;
  organizationsStore: OrganizationsStore;
  usersStore: UsersStore;
  inviteStore: InviteStore;
  analyticsStore: AnalyticsStore;
  roleStore: RoleStore;
  startupService: StartupService;
  startupStore: StartupStore;
  toursStore: ToursStore;
};

const StoresContext = createContext<Stores | null>(null);
const langResources = {
  en: {
    translation: translationEn,
  },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : '';
};
export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

async function RenderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();

  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance();
  const notificationStore = new NotificationsStore();
  const startupService = new StartupService(apiServiceAxiosInstance);
  const startupStore = new StartupStore(authenticationService, configurationService, startupService, notificationStore);
  const analyticsStore = new AnalyticsStore(startupStore, configurationService);
  const organizationsStore = new OrganizationsStore(notificationStore, analyticsStore);
  const confirmDiscardingChangesDialogStore = new ConfirmDiscardingChangesDialogStore();
  const usersStore = new UsersStore(notificationStore, analyticsStore);
  const inviteStore = new InviteStore(notificationStore, organizationsStore, analyticsStore);
  const roleStore = new RoleStore(analyticsStore);
  const toursStore = new ToursStore(analyticsStore);

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    organizationsStore,
    confirmDiscardingChangesDialogStore,
    usersStore,
    inviteStore,
    analyticsStore,
    roleStore,
    startupStore,
    startupService,
    toursStore,
  };
  
  await i18next.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(navigator.language.slice(0, 2)) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance, authenticationService, errorHandler);
  const root = ReactDOM.createRoot(rootElement);
  
  root.render(
    <AppErrorBoundary>
      <BrowserRouter>
        <StoresContext.Provider value={stores}>
          <App>{RoutesModule.routes}</App>
        </StoresContext.Provider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

RenderApp();
